<script setup lang="ts">
import type { NavItem } from "../types/navigation";

const { user: userInfo } = useUser();
const { routes, abilities, user } = userInfo.value;
const { activeSourcingMessageNotification } = useNavigation();

const isFHD = useMatchMedia("(min-width: 1920px");
const { isOpen } = useNavigation();
const variant = computed(() => (isOpen.value ? "extended" : "collapsed"));
const showContactPersonModal = ref(false);

const jobNavItems: NavItem[] = [
  {
    elementDetail: "Posting overview",
    id: "1",
    title: "Übersicht",
    to: routes.jobAds.overview,
  },
  {
    elementDetail: "Job ad report",
    id: "2",
    title: "Kennzahlen",
    to: routes.jobAds.metrics,
  },
];
if (abilities.job) {
  jobNavItems.push({
    elementDetail: "Create new posting",
    id: "3",
    title: "Neues Inserat schalten",
    to: routes.jobAds.create,
  });
}

const profileNavItems: NavItem[] = [];
if (abilities.invoice) {
  profileNavItems.push({
    elementDetail: "Invoices",
    id: "2",
    title: "Rechnungen",
    to: routes.profile.invoices,
  });
}
profileNavItems.push(
  {
    elementDetail: "Companydata edit",
    id: "4",
    title: "Firmendaten bearbeiten",
    to: routes.profile.company,
  },
  {
    id: "5",
    title: "Einstellungen",
    to: routes.profile.settings,
  },
  {
    as: "button",
    elementDetail: "Logout",
    id: "6",
    method: "post",
    title: "Abmelden",
    to: "/api/auth/oauth/logout",
  },
);

const activeSourcingNavItems: NavItem[] = [
  {
    title: "Talentsuche",
    to: "/p/active-sourcing/search?resetSearch=true",
  },
  {
    notification: activeSourcingMessageNotification.value,
    title: "Nachrichten",
    to: "/p/active-sourcing/messages",
  },
  {
    title: "Projekte",
    to: "/p/active-sourcing/projects",
  },
  {
    title: "Matching",
    to: "/p/active-sourcing/matching",
  },
  {
    title: "Meine Suchen",
    to: "/p/active-sourcing/saved-search",
  },
  {
    title: "Versionshinweise",
    to: "/p/active-sourcing/releases",
  },
].map((NavItem, index) => ({
  ...NavItem,
  id: `${index + 1}`,
}));

const employerBrandingNavItems: NavItem[] = [];

if (abilities.businessPage) {
  employerBrandingNavItems.push({
    elementDetail: "Arbeitgeberprofil",
    id: "1",
    title: "employer.page",
    to: routes.employerBranding.employerPage,
  });
}

if (abilities.businessPagePlus) {
  employerBrandingNavItems.push({
    id: "2",
    title: "Arbeitgeberprofil",
    to: routes.employerBranding.employerPagePlus,
  });
  employerBrandingNavItems.push({
    id: "5",
    title: "Kennzahlen",
    to: routes.employerBranding.metrics,
  });
}

if (abilities.employerBrandingBasic) {
  employerBrandingNavItems.push({
    id: "3",
    title: "Arbeitgeberprofil Basic",
    to: routes.employerBranding.employerBrandingBasic,
  });
}

if (abilities.employerBrandingPlus) {
  employerBrandingNavItems.push({
    id: "4",
    title: "Arbeitgeberprofil",
    to: routes.employerBranding.employerBrandingPlus,
  });
  employerBrandingNavItems.push({
    id: "5",
    title: "Kennzahlen",
    to: routes.employerBranding.metrics,
  });
}

const recruitingServicesNavItems: NavItem[] = [
  {
    elementDetail: "Recruting Services",
    id: "1",
    title: "bewerber.manager",
    to: routes.recruitingServices.applicantManager,
  },
];

if (user.company.distributionChannel === "DIRECT-SALES") {
  recruitingServicesNavItems.push({
    elementDetail: "Recruting Services",
    id: "1",
    title: "smart bewerben",
    to: routes.recruitingServices.oneClickApplication,
  });
}

if (user.relatedCompanies && user.relatedCompanies?.length > 1) {
  profileNavItems.splice(2, 0, {
    id: "4",
    title: "Unternehmen wechseln",
    to: routes.profile.companySwitch,
  });
}

profileNavItems.splice(2, 0, {
  click: () => {
    showContactPersonModal.value = true;
  },
  id: "1",
  title: "Ansprechperson",
  to: "#",
});

// Watch for changes on activeSourcingMessageNotification
watch(activeSourcingMessageNotification, (newNotification) => {
  // Find the item with title 'Nachrichten'
  const messageItem = activeSourcingNavItems.find(
    (item) => item.title === "Nachrichten",
  );
  if (messageItem) {
    messageItem.notification = newNotification;
  }
});
</script>

<template>
  <TheNavShell>
    <!-- main content  -->
    <div
      :class="[
        'flex grow flex-col justify-between',
        isOpen ? 'mt-6' : 'mt-4',
      ]"
    >
      <!-- user details -->
      <div
        :class="`flex flex-col ${isOpen ? 'gap-5' : 'gap-10'} text-center`"
      >
        <div>
          <!-- profile image -->
        <BaseProfileImage
            :src="user.avatar"
            alt="Profilbild"
            :size="isOpen ? 'xl' : 'large'"
            class="mx-auto"
        />
          <div v-if="isOpen" class="mt-2">
            <BaseText variant="base-500" data-qa="mainNav name">
              {{ user.firstName }} {{ user.lastName }}
            </BaseText>

            <BaseText color="text-gray-600" data-qa="mainNav bio">
              {{ user.company.fullName }}
            </BaseText>

            <BaseText color="text-gray-600" data-qa="mainNav location">
              {{ user.company.mainAddress?.city }}
            </BaseText>
          </div>
        </div>

        <div
          :class="`divide-black divide-y flex flex-col ${
            !isOpen ? 'mt-[5rem]' : ''
          }`"
        >
          <!-- dashboard and profile links  -->
          <div class="flex flex-col gap-2 pb-4">
            <TheNavLink
              title="Übersicht"
              icon-name="home"
              :to="routes.dashboard"
              :variant="variant"
              :active="false"
            />
            <TheNavCollapse
              title="Profil"
              icon-name="profile"
              :children="profileNavItems"
              :variant="variant"
              data-qa="mainNav profile links"
              data-gtm-element="BP: Navigation"
              data-gtm-element-detail="Profile"
            />
          </div>

          <!-- jobs and contingent links -->
          <div class="flex flex-col gap-2 py-4">
            <TheNavCollapse
              title="Inserate"
              icon-name="job-ad-edit"
              :children="jobNavItems"
              :variant="variant"
              data-qa="mainNav service links"
              data-gtm-element="BP: Navigation"
              data-gtm-element-detail="Posting"
            />
            <TheNavLink
              title="Guthaben"
              icon-name="credit"
              :to="routes.credits"
              :variant="variant"
              :active="false"
              data-gtm-element="BP: Navigation"
              data-gtm-element-detail="Credit"
            />
          </div>

          <!-- active sourcing, employer.branding and recruiting service links -->
          <div class="flex flex-col gap-2 py-4">
            <TheNavCollapse
              title="Talentdatenbank"
              icon-name="magnifier"
              :children="activeSourcingNavItems"
              :variant="variant"
              data-base-tour="Talentdatenbank"
              data-qa="mainNav kandidatensuche link"
            />
            <TheNavCollapse
              v-if="
                abilities.businessPage ||
                abilities.businessPagePlus ||
                abilities.employerBrandingBasic ||
                abilities.employerBrandingPlus
              "
              title="Arbeitgeberprofil"
              icon-name="building"
              :children="employerBrandingNavItems"
              :variant="variant"
              data-qa="mainNav Arbeitgeberprofil link"
            />
            <TheNavCollapse
              title="Recruiting Services"
              icon-name="tool-box"
              :children="recruitingServicesNavItems"
              :variant="variant"
              data-qa="mainNav recruiting services link"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- footer  -->
    <div>
      <div class="flex flex-col gap-2">
        <!-- Temporary solution. Remove when we introduce i18n globally. -->
        <slot />
        <TheNavLink
          title="FAQs"
          icon-name="speech-bubble"
          :to="routes.faqs"
          :variant="variant"
          :external="true"
          data-qa="mainNav faqs"
          data-gtm-element="BP: Navigation"
          data-gtm-element-detail="FAQs"
        />
        <TheNavLink
          title="AGB und Impressum"
          icon-name="paper"
          :to="routes.agb"
          :variant="variant"
          :external="true"
          data-qa="mainNav agb"
          data-gtm-element="BP: Navigation"
          data-gtm-element-detail="Conditions and Imprint"
        />
      </div>
    </div>
    <TheContactPersonModal
      :open="showContactPersonModal"
      data-gtm-element="BP: Navigation"
      data-gtm-element-detail="Contactperson"
      @close-modal="showContactPersonModal = false"
    />
  </TheNavShell>
</template>
